import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { downscaleImage } from '../lib/downscale-image.js';

// ─── SOP Edit Modal ───────────────────────────────────────────────────────────
export function SOPEditModal({ sopId, onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [steps, setSteps] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api('GET', `/api/sop/${sopId}`).then(data => {
      setTitle(data.title);
      setSteps(data.steps.map(s => ({ id: s.id, description: s.description, image_url: s.image_url || '' })));
      setLoaded(true);
    });
  }, [sopId]);

  function moveUp(i) {
    if (i === 0) return;
    setSteps(s => { const a = [...s]; [a[i-1], a[i]] = [a[i], a[i-1]]; return a; });
  }
  function moveDown(i) {
    setSteps(s => { if (i >= s.length - 1) return s; const a = [...s]; [a[i], a[i+1]] = [a[i+1], a[i]]; return a; });
  }
  function updateStep(i, field, val) {
    setSteps(s => { const a = [...s]; a[i] = { ...a[i], [field]: val }; return a; });
  }
  function removeStep(i) {
    setSteps(s => {
      const step = s[i];
      if (step.id) setDeletedIds(d => [...d, step.id]);
      return s.filter((_, j) => j !== i);
    });
  }
  function addStep() { setSteps(s => [...s, { id: null, description: '', image_url: '' }]); }
  // Shrunk first: an unshrunk phone photo is over the app's 1 MB body limit
  // and the step save failed (CJ 2026-09-30).
  async function handleStepImage(i, file) {
    if (!file) return;
    const { base64 } = await downscaleImage(file);
    updateStep(i, 'image_url', base64);
  }

  async function handleSave() {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await api('PUT', `/api/sops/${sopId}`, { title });
      for (const id of deletedIds) await api('DELETE', `/api/sop-steps/${id}`);
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        if (step.id) {
          await api('PUT', `/api/sop-steps/${step.id}`, { description: step.description, image_url: step.image_url || null, step_number: i + 1 });
        } else {
          await api('POST', `/api/sops/${sopId}/steps`, { description: step.description, image_url: step.image_url || null, step_number: i + 1 });
        }
      }
      onSaved();
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-cream w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col fade-in shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-crimson rounded-t-2xl p-5 flex items-center justify-between flex-shrink-0">
          <h3 className="text-cream font-bold text-lg">Edit SOP</h3>
          <button onClick={onClose} className="text-cream/70 hover:text-cream text-3xl leading-none">×</button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {!loaded ? (
            <div className="text-center text-gray-400 py-8">Loading...</div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">SOP Title</label>
                <input value={title} onChange={e => setTitle(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-crimson/30"
                  placeholder="SOP Title" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Steps</label>
                <div className="space-y-2">
                  {steps.map((step, i) => (
                    <div key={i} className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-5 text-right flex-shrink-0 font-bold">{i+1}.</span>
                        <input value={step.description} onChange={e => updateStep(i, 'description', e.target.value)}
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"
                          placeholder="Step description" />
                        <div className="flex gap-1 flex-shrink-0">
                          <button type="button" onClick={() => moveUp(i)} disabled={i === 0}
                            className="w-7 h-7 rounded text-xs bg-gray-100 hover:bg-gray-200 disabled:opacity-30 transition-all">↑</button>
                          <button type="button" onClick={() => moveDown(i)} disabled={i === steps.length - 1}
                            className="w-7 h-7 rounded text-xs bg-gray-100 hover:bg-gray-200 disabled:opacity-30 transition-all">↓</button>
                          <button type="button" onClick={() => removeStep(i)}
                            className="w-7 h-7 rounded text-xs bg-red-50 text-red-500 hover:bg-red-100 transition-all">×</button>
                        </div>
                      </div>
                      <div className="flex gap-2 items-center pl-7">
                        <span className="text-xs text-gray-400">📷</span>
                        <input type="file" accept="image/*" onChange={e => handleStepImage(i, e.target.files[0])}
                          className="flex-1 text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium file:bg-gray-100 file:text-gray-600 hover:file:bg-gray-200 cursor-pointer" />
                        {step.image_url && (
                          <img src={step.image_url} alt="Preview" className="w-10 h-8 object-cover rounded border border-gray-200 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={addStep} className="text-sm text-crimson hover:text-crimson-dark font-medium">+ Add Step</button>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="p-5 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving || !title.trim()}
            className="flex-1 py-3 rounded-xl bg-crimson text-white font-semibold hover:bg-crimson-dark disabled:opacity-50 active:scale-98 transition-all">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
